import { QUESTIONNAIRES, RECORDING_MODES } from "../studies/studyconfig";
import { formatQuestionnaireTypeFallback } from "../patientProfileData";

const RECORDING_MODE_LABELS_BY_VALUE = new Map(
  RECORDING_MODES.map((mode) => [mode.value, mode]),
);
const QUESTIONNAIRE_LABELS_BY_VALUE = new Map(
  QUESTIONNAIRES.map((questionnaire) => [questionnaire.value, questionnaire]),
);

export const getMonthCounts = (timestamps) => {
  if (!timestamps || timestamps.length === 0) {
    return { current: 0, previous: 0 };
  }

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;

  let current = 0;
  let previous = 0;

  timestamps.forEach((timestamp) => {
    const date = new Date(timestamp);

    if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
      current += 1;
      return;
    }

    if (date.getMonth() === prevMonth && date.getFullYear() === prevYear) {
      previous += 1;
    }
  });

  return { current, previous };
};

const getBgClass = (num) => {
  if (num >= 9) {
    return "bg-green-200 text-green-900";
  }

  if (num >= 3) {
    return "bg-orange-200 text-orange-900";
  }

  return "bg-red-200 text-red-900";
};

export const getAdherenceCounts = (summary, fallbackCount) => {
  if (
    summary &&
    typeof summary.expectedItemsCount === "number" &&
    typeof summary.completedItemsCount === "number" &&
    summary.expectedItemsCount > 0
  ) {
    return {
      completed: summary.completedItemsCount,
      expected: summary.expectedItemsCount,
      ratio:
        typeof summary.completionRatio === "number"
          ? summary.completionRatio
          : 0,
    };
  }

  if (
    summary &&
    typeof summary.expectedModesCount === "number" &&
    typeof summary.completedModesCount === "number" &&
    summary.expectedModesCount > 0
  ) {
    return {
      completed: summary.completedModesCount,
      expected: summary.expectedModesCount,
      ratio:
        typeof summary.completionRatio === "number"
          ? summary.completionRatio
          : 0,
    };
  }

  if (
    summary &&
    typeof summary.expectedQuestionnairesCount === "number" &&
    typeof summary.completedQuestionnairesCount === "number" &&
    summary.expectedQuestionnairesCount > 0
  ) {
    return {
      completed: summary.completedQuestionnairesCount,
      expected: summary.expectedQuestionnairesCount,
      ratio:
        summary.expectedQuestionnairesCount > 0
          ? summary.completedQuestionnairesCount /
            summary.expectedQuestionnairesCount
          : 0,
    };
  }

  return {
    completed: fallbackCount,
    expected: null,
    ratio: null,
  };
};

export const getAdherenceBgClass = (summary, fallbackCount) => {
  const { completed, expected } = getAdherenceCounts(summary, fallbackCount);

  if (expected === null) {
    return getBgClass(fallbackCount);
  }

  if (expected > 0 && completed === expected) {
    return "bg-green-200 text-green-900";
  }

  if (completed > 0) {
    return "bg-orange-200 text-orange-900";
  }

  return "bg-red-200 text-red-900";
};

export const getComparableTimestamp = (value) => {
  const timestamp = Date.parse(value || "");
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

export const uniqueNonEmptyStrings = (values) => {
  const seen = new Set();

  return values.reduce((result, value) => {
    if (typeof value !== "string") {
      return result;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue || seen.has(trimmedValue)) {
      return result;
    }

    seen.add(trimmedValue);
    result.push(trimmedValue);
    return result;
  }, []);
};

const SYNTHETIC_PATIENT_UID_PREFIXES = ["precreated-", "invite:"];

const normalizePatientUid = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const uid = value.trim();

  if (!uid) {
    return null;
  }

  return SYNTHETIC_PATIENT_UID_PREFIXES.some((prefix) => uid.startsWith(prefix))
    ? null
    : uid;
};

export const getClaimedPatientUid = (patient) => normalizePatientUid(patient?.uid);

export const hasClaimedPatientAccount = (patient) =>
  Boolean(getClaimedPatientUid(patient));

export const didPatientAccountExistInPreviousMonth = (
  patient,
  now = new Date(),
) => {
  if (!hasClaimedPatientAccount(patient)) {
    return false;
  }

  const startOfCurrentMonthTimestamp = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    1,
  );

  const associationStartedAtTimestamp = getComparableTimestamp(
    patient?.associationStartedAt,
  );
  const subjectRecordCreatedAtTimestamp = getComparableTimestamp(
    patient?.subjectRecordCreatedAt,
  );
  const createdAtTimestamp = getComparableTimestamp(patient?.createdAt);
  const updatedAtTimestamp = getComparableTimestamp(patient?.updatedAt);
  const lastLoginAtTimestamp = getComparableTimestamp(patient?.lastLoginAt);

  let effectiveStartTimestamp = Number.isFinite(associationStartedAtTimestamp)
    ? associationStartedAtTimestamp
    : Number.isFinite(subjectRecordCreatedAtTimestamp)
      ? subjectRecordCreatedAtTimestamp
      : createdAtTimestamp;

  const hasPriorHistoryEvidence =
    (Array.isArray(patient?.timestamps) ? patient.timestamps : []).some(
      (timestamp) => {
        const comparableTimestamp = getComparableTimestamp(timestamp);
        return (
          Number.isFinite(comparableTimestamp) &&
          comparableTimestamp < startOfCurrentMonthTimestamp
        );
      },
    ) ||
    [
      patient?.lastMeasurementAt,
      patient?.lastQuestionnaireAt,
      patient?.lastCatAt,
      patient?.lastSgrqAt,
    ].some(
      (timestamp) => {
        const comparableTimestamp = getComparableTimestamp(timestamp);
        return (
          Number.isFinite(comparableTimestamp) &&
          comparableTimestamp < startOfCurrentMonthTimestamp
        );
      },
    ) ||
    Number(patient?.previousMonthCount ?? 0) > 0;

  const canUseLegacyAssociationFallback =
    !Number.isFinite(associationStartedAtTimestamp) &&
    !Number.isFinite(subjectRecordCreatedAtTimestamp) &&
    Number.isFinite(updatedAtTimestamp) &&
    Number.isFinite(lastLoginAtTimestamp) &&
    updatedAtTimestamp >= startOfCurrentMonthTimestamp &&
    lastLoginAtTimestamp >= startOfCurrentMonthTimestamp &&
    !hasPriorHistoryEvidence;

  if (canUseLegacyAssociationFallback) {
    effectiveStartTimestamp = updatedAtTimestamp;
  }

  if (!Number.isFinite(effectiveStartTimestamp)) {
    return true;
  }

  return effectiveStartTimestamp < startOfCurrentMonthTimestamp;
};

const getClaimedPatientUids = (patient) =>
  uniqueNonEmptyStrings([
    ...(Array.isArray(patient?.uids) ? patient.uids : []),
    patient?.uid,
  ]).reduce((result, value) => {
    const uid = normalizePatientUid(value);

    if (uid) {
      result.push(uid);
    }

    return result;
  }, []);

const mergeQualitySummary = (baseSummary, nextSummary) => ({
  signal: {
    pass: (baseSummary?.signal?.pass ?? 0) + (nextSummary?.signal?.pass ?? 0),
    fail: (baseSummary?.signal?.fail ?? 0) + (nextSummary?.signal?.fail ?? 0),
  },
  spiro: {
    pass: (baseSummary?.spiro?.pass ?? 0) + (nextSummary?.spiro?.pass ?? 0),
    fail: (baseSummary?.spiro?.fail ?? 0) + (nextSummary?.spiro?.fail ?? 0),
  },
});

export const getRecordingModeLabel = (mode, t) => {
  const config = RECORDING_MODE_LABELS_BY_VALUE.get(mode);
  return config
    ? t(config.labelKey, { defaultValue: config.labelDefault })
    : mode;
};

export const getQuestionnaireLabel = (questionnaireId, t) => {
  const config = QUESTIONNAIRE_LABELS_BY_VALUE.get(questionnaireId);
  return config
    ? t(config.labelKey, { defaultValue: config.labelDefault })
    : formatQuestionnaireTypeFallback(questionnaireId);
};

export const getProtocolFrequencyLabel = (frequency, t) =>
  frequency
    ? t(`studies.questionnaires.frequency.${frequency}`, frequency)
    : "—";

export const getOccurrencesPerPeriodLabel = (occurrencesPerPeriod, t) => {
  const normalizedOccurrencesPerPeriod = Number(occurrencesPerPeriod);

  if (
    !Number.isInteger(normalizedOccurrencesPerPeriod) ||
    normalizedOccurrencesPerPeriod <= 1
  ) {
    return null;
  }

  return t("patientsPage.adherencePanelOccurrencesPerPeriod", {
    count: normalizedOccurrencesPerPeriod,
    defaultValue: "{{count}} per period",
  });
};

export const buildAdherenceScheduleLabel = (
  frequency,
  occurrencesPerPeriod,
  t,
) => {
  const frequencyLabel = getProtocolFrequencyLabel(frequency, t);
  const occurrencesLabel = getOccurrencesPerPeriodLabel(
    occurrencesPerPeriod,
    t,
  );

  return [frequencyLabel !== "—" ? frequencyLabel : null, occurrencesLabel]
    .filter(Boolean)
    .join(" · ") || "—";
};

export const formatAdherenceProgress = (completedCount, expectedCount, t) =>
  t("patientsPage.adherencePanelOccurrenceProgress", {
    completed: Number(completedCount) || 0,
    expected: Number(expectedCount) || 0,
    defaultValue: "{{completed}}/{{expected}}",
  });

export const getRecordingAdherenceSummary = (summary) => {
  if (summary?.recordings) {
    return summary.recordings;
  }

  return Array.isArray(summary?.modes) ? summary : null;
};

export const getQuestionnaireAdherenceSummary = (summary) =>
  summary?.questionnaires || null;

export const hasRecordingAdherenceItems = (summary) =>
  Array.isArray(summary?.modes) && summary.modes.length > 0;

export const hasQuestionnaireAdherenceItems = (summary) =>
  Array.isArray(summary?.assignments) && summary.assignments.length > 0;

export const buildPatientIdentityKey = (patient, index = 0) => {
  if (!patient || typeof patient !== "object") {
    return "";
  }

  const subjectKey =
    typeof patient?.subjectKey === "string" ? patient.subjectKey.trim() : "";

  if (subjectKey) {
    return `subject:${subjectKey}`;
  }

  const clinicStudyId =
    typeof patient?.clinicStudyId === "string"
      ? patient.clinicStudyId.trim()
      : "";
  const sid = typeof patient?.sid === "string" ? patient.sid.trim() : "";

  if (sid && sid.toUpperCase() !== "N/A") {
    return clinicStudyId ? `study:${clinicStudyId}:sid:${sid}` : `sid:${sid}`;
  }

  const uid = typeof patient?.uid === "string" ? patient.uid.trim() : "";

  if (uid) {
    return `uid:${uid}`;
  }

  return `row:${index}`;
};

const buildPatientGroupKey = (patient, index) =>
  buildPatientIdentityKey(patient, index);

export const mergePatientsBySubject = (patients) => {
  const groupedPatients = new Map();

  patients.forEach((patient, index) => {
    const patientHasLinkedAccount = hasClaimedPatientAccount(patient);
    const nextSubjectRecordCreatedAt = patientHasLinkedAccount
      ? null
      : patient?.createdAt || null;
    const groupKey = buildPatientGroupKey(patient, index);
    const nextEmails = uniqueNonEmptyStrings([
      ...(Array.isArray(patient?.emails) ? patient.emails : []),
      patient?.email,
    ]);
    const nextUids = getClaimedPatientUids(patient);
    const nextTimestamps = Array.isArray(patient?.timestamps)
      ? patient.timestamps.filter(Boolean)
      : [];
    const existingPatient = groupedPatients.get(groupKey);

    if (!existingPatient) {
      groupedPatients.set(groupKey, {
        ...patient,
        hasLinkedAccount: patientHasLinkedAccount,
        uid: nextUids[0] || null,
        uids: nextUids,
        email: nextEmails[0] || patient?.email || "",
        emails: nextEmails,
        subjectRecordCreatedAt: nextSubjectRecordCreatedAt,
        timestamps: [...nextTimestamps].sort(
          (left, right) =>
            getComparableTimestamp(right) - getComparableTimestamp(left),
        ),
        currentMonthQualitySummary: mergeQualitySummary(
          null,
          patient?.currentMonthQualitySummary,
        ),
        previousMonthQualitySummary: mergeQualitySummary(
          null,
          patient?.previousMonthQualitySummary,
        ),
      });
      return;
    }

    const mergedEmails = uniqueNonEmptyStrings([
      ...existingPatient.emails,
      ...nextEmails,
    ]);
    const mergedUids = uniqueNonEmptyStrings([
      ...existingPatient.uids,
      ...nextUids,
    ]);
    const mergedTimestamps = [
      ...existingPatient.timestamps,
      ...nextTimestamps,
    ].sort(
      (left, right) =>
        getComparableTimestamp(right) - getComparableTimestamp(left),
    );
    const nextLastMeasurementAt =
      getComparableTimestamp(patient?.lastMeasurementAt) >
      getComparableTimestamp(existingPatient.lastMeasurementAt)
        ? patient?.lastMeasurementAt || null
        : existingPatient.lastMeasurementAt;
    const useNextCat =
      getComparableTimestamp(patient?.lastCatAt) >
      getComparableTimestamp(existingPatient.lastCatAt);
    const useNextSgrq =
      getComparableTimestamp(patient?.lastSgrqAt) >
      getComparableTimestamp(existingPatient.lastSgrqAt);
    const useNextQuestionnaire =
      getComparableTimestamp(patient?.lastQuestionnaireAt) >
      getComparableTimestamp(existingPatient.lastQuestionnaireAt);
    const mergedHasLinkedAccount =
      existingPatient.hasLinkedAccount === true || patientHasLinkedAccount;
    const preferredIdentityPatient =
      existingPatient.hasLinkedAccount === true
        ? existingPatient
        : patientHasLinkedAccount
          ? patient
          : existingPatient;
    const preferredIdentityUid = getClaimedPatientUid(preferredIdentityPatient);
    const preferredIdentityCreatedAt =
      preferredIdentityPatient?.createdAt ||
      existingPatient.createdAt ||
      patient?.createdAt ||
      null;
    const mergedAssociationStartedAt = [
      existingPatient.associationStartedAt,
      patient?.associationStartedAt,
    ]
      .filter(Boolean)
      .sort(
        (left, right) =>
          getComparableTimestamp(left) - getComparableTimestamp(right),
      )[0] || null;
    const mergedSubjectRecordCreatedAt = [
      existingPatient.subjectRecordCreatedAt,
      nextSubjectRecordCreatedAt,
    ]
      .filter(Boolean)
      .sort(
        (left, right) =>
          getComparableTimestamp(left) - getComparableTimestamp(right),
      )[0] || null;

    groupedPatients.set(groupKey, {
      ...existingPatient,
      hasLinkedAccount: mergedHasLinkedAccount,
      sid: existingPatient.sid || patient?.sid || "N/A",
      uid:
        preferredIdentityUid || mergedUids[0] || existingPatient.uid || null,
      uids: mergedUids,
      email:
        preferredIdentityPatient?.email ||
        mergedEmails[0] ||
        existingPatient.email ||
        patient?.email ||
        "",
      createdAt: preferredIdentityCreatedAt,
      associationStartedAt: mergedAssociationStartedAt,
      updatedAt:
        getComparableTimestamp(patient?.updatedAt) >
        getComparableTimestamp(existingPatient.updatedAt)
          ? patient?.updatedAt || null
          : existingPatient.updatedAt,
      lastLoginAt:
        getComparableTimestamp(patient?.lastLoginAt) >
        getComparableTimestamp(existingPatient.lastLoginAt)
          ? patient?.lastLoginAt || null
          : existingPatient.lastLoginAt,
      subjectRecordCreatedAt: mergedSubjectRecordCreatedAt,
      emails: mergedEmails,
      measurementsCount:
        (existingPatient.measurementsCount ?? 0) +
        (patient?.measurementsCount ?? 0),
      lastMeasurementAt: nextLastMeasurementAt,
      timestamps: mergedTimestamps,
      currentMonthCount:
        (existingPatient.currentMonthCount ?? 0) +
        (patient?.currentMonthCount ?? 0),
      previousMonthCount:
        (existingPatient.previousMonthCount ?? 0) +
        (patient?.previousMonthCount ?? 0),
      currentMonthQualitySummary: mergeQualitySummary(
        existingPatient.currentMonthQualitySummary,
        patient?.currentMonthQualitySummary,
      ),
      previousMonthQualitySummary: mergeQualitySummary(
        existingPatient.previousMonthQualitySummary,
        patient?.previousMonthQualitySummary,
      ),
      lastCatAt: useNextCat
        ? patient?.lastCatAt || null
        : existingPatient.lastCatAt,
      lastCatScore: useNextCat
        ? (patient?.lastCatScore ?? null)
        : existingPatient.lastCatScore,
      lastSgrqAt: useNextSgrq
        ? patient?.lastSgrqAt || null
        : existingPatient.lastSgrqAt,
      lastQuestionnaireAt: useNextQuestionnaire
        ? patient?.lastQuestionnaireAt || null
        : existingPatient.lastQuestionnaireAt,
      lastQuestionnaireType: useNextQuestionnaire
        ? patient?.lastQuestionnaireType || null
        : existingPatient.lastQuestionnaireType,
      currentMonthAdherence:
        existingPatient.currentMonthAdherence || patient?.currentMonthAdherence,
      previousMonthAdherence:
        existingPatient.previousMonthAdherence ||
        patient?.previousMonthAdherence,
      active: existingPatient.active || patient?.active === true,
    });
  });

  return [...groupedPatients.values()];
};

export const formatQualitySummaryTooltip = (summary) => {
  if (!summary) {
    return "";
  }

  return [
    `Signal pass/fail: ${summary.signal?.pass ?? 0}/${summary.signal?.fail ?? 0}`,
    `Spiro pass/fail: ${summary.spiro?.pass ?? 0}/${summary.spiro?.fail ?? 0}`,
  ].join("\n");
};

export const getLastQuestionnaireSummary = (patient) => {
  const lastQuestionnaireAt =
    patient?.lastQuestionnaireAt ||
    (getComparableTimestamp(patient?.lastCatAt) >
    getComparableTimestamp(patient?.lastSgrqAt)
      ? patient?.lastCatAt || null
      : patient?.lastSgrqAt || null);

  if (lastQuestionnaireAt === patient?.lastQuestionnaireAt) {
    return {
      takenAt: lastQuestionnaireAt,
      questionnaireType: patient?.lastQuestionnaireType || null,
    };
  }

  return {
    takenAt: lastQuestionnaireAt,
    questionnaireType:
      lastQuestionnaireAt === patient?.lastCatAt
        ? "cat"
        : lastQuestionnaireAt === patient?.lastSgrqAt
          ? "sgrq"
          : null,
  };
};

export const formatAdherenceBadgeValue = (summary, fallbackCount) => {
  const { completed, expected } = getAdherenceCounts(summary, fallbackCount);

  if (expected === null) {
    return String(fallbackCount);
  }

  return `${completed}/${expected}`;
};

export const getAdherenceSortValue = (summary, fallbackCount) => {
  const { completed, expected, ratio } = getAdherenceCounts(
    summary,
    fallbackCount,
  );

  if (expected === null) {
    return fallbackCount;
  }

  return ratio * 1000 + completed;
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export const isPatientActive = (
  patient,
  fallbackCurrentMonthCount,
  now = new Date(),
) => {
  const lastMeasurementTimestamp = getComparableTimestamp(
    patient?.lastMeasurementAt,
  );

  if (Number.isFinite(lastMeasurementTimestamp)) {
    return lastMeasurementTimestamp >= now.getTime() - THIRTY_DAYS_MS;
  }

  if (typeof patient?.active === "boolean") {
    return patient.active;
  }

  return (patient?.currentMonthCount ?? fallbackCurrentMonthCount ?? 0) > 0;
};

export const getPatientClinicStudyDisplay = (patient) => {
  const clinicStudyIds = Array.isArray(patient?.clinicStudyIds)
    ? uniqueNonEmptyStrings(patient.clinicStudyIds)
    : [];

  if (clinicStudyIds.length > 0) {
    return clinicStudyIds.join(", ");
  }

  if (
    typeof patient?.clinicStudyId === "string" &&
    patient.clinicStudyId.trim().length > 0
  ) {
    return patient.clinicStudyId.trim();
  }

  return "—";
};

export const formatAdherenceTooltip = (summary, fallbackQualitySummary, t) => {
  const recordingSummary = getRecordingAdherenceSummary(summary);
  const questionnaireSummary = getQuestionnaireAdherenceSummary(summary);
  const hasRecordingSummary = hasRecordingAdherenceItems(recordingSummary);
  const hasQuestionnaireSummary =
    hasQuestionnaireAdherenceItems(questionnaireSummary);

  if (!hasRecordingSummary && !hasQuestionnaireSummary) {
    return formatQualitySummaryTooltip(fallbackQualitySummary);
  }

  const overallCounts = getAdherenceCounts(summary, 0);
  const lines = [
    `${t("patientsPage.adherenceOverallLabel", {
      defaultValue: "Adherence",
    })}: ${overallCounts.completed}/${overallCounts.expected}`,
  ];

  if (hasRecordingSummary) {
    lines.push(
      `${t("patientsPage.adherencePanelRecordingsSectionTitle", {
        defaultValue: "Recordings",
      })}: ${recordingSummary.completedModesCount}/${recordingSummary.expectedModesCount}`,
    );
    lines.push(
      ...recordingSummary.modes.map((modeSummary) => {
        const baseLabel = getRecordingModeLabel(modeSummary.mode, t);
        const scheduleLabel = buildAdherenceScheduleLabel(
          modeSummary.frequency,
          modeSummary.occurrencesPerPeriod,
          t,
        );
        const statusLabel = formatAdherenceProgress(
          modeSummary.recordingsCount,
          modeSummary.expectedCount,
          t,
        );

        return `${baseLabel}: ${statusLabel}${
          scheduleLabel !== "—" ? `, ${scheduleLabel}` : ""
        }`;
      }),
    );
  }

  if (hasQuestionnaireSummary) {
    lines.push(
      `${t("patientsPage.adherencePanelQuestionnairesSectionTitle", {
        defaultValue: "Questionnaires",
      })}: ${questionnaireSummary.completedQuestionnairesCount}/${questionnaireSummary.expectedQuestionnairesCount}`,
    );
    lines.push(
      ...questionnaireSummary.assignments.map((assignment) => {
        const baseLabel = getQuestionnaireLabel(
          assignment.questionnaireId,
          t,
        );
        const scheduleLabel = buildAdherenceScheduleLabel(
          assignment.frequency,
          assignment.occurrencesPerPeriod,
          t,
        );
        const statusLabel = formatAdherenceProgress(
          assignment.responsesCount,
          assignment.expectedCount,
          t,
        );

        return `${baseLabel}: ${statusLabel}${
          scheduleLabel !== "—" ? `, ${scheduleLabel}` : ""
        }`;
      }),
    );
  }

  return lines.join("\n");
};
